import { HttpErrorResponse } from "@angular/common/http";
import { Component, ViewChild } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { AccessControlService } from "app/access-control/access-control-service";
import { AuthService } from "app/auth/auth.service";
import { IUser } from "app/auth/user.model";
import {
  AlertController,
  Events,
  LoadingController,
  ModalController,
  NavController,
  NavParams,
} from "ionic-angular";
import { LookupValue } from "models/common/lookup-value";
import { ContactType } from "models/company-resources/contact-type";
import EmployeeDetail from "models/company-resources/employee-detail";
import CredentialedResource from "models/resource-management/credentialed-resource";
import { RadiusMeasurement } from "models/resource-management/volunteers/radius-measurement";
import Volunteer from "models/resource-management/volunteers/volunteer";
import VolunteerAvailabilityPreference from "models/resource-management/volunteers/volunteer-availability-preference";
import VolunteerLocationPreference from "models/resource-management/volunteers/volunteer-location-preference";
import VolunteerPreferences from "models/resource-management/volunteers/volunteer-preferences";
import VolunteerSchedule from "models/resource-management/volunteers/volunteer-schedule";
import { VolunteerSkillInterest } from "models/resource-management/volunteers/volunteer-skill-interest";
import WorkforceResource from "models/system-admin/workforce-resource";
import moment from "moment";
import CompanyResourcePage from "pages/company-resources/company-resource-page";
import { EmployeeHealthContactDetails } from "pages/company-resources/contact-details/employee-health/employee-health-contact-details";
import { commonVar } from "providers/common-var/common";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { PatientChartService } from "providers/patient-charts/patient-chart.service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { UsersService } from "providers/users-service/users.service";
import { Subscription } from "rxjs";
import { PERMISSION_GROUPS_COGNITO } from "../../../../app/access-control/common-permission-asks";
import { Workforce } from "../../../../app/access-control/permissions";
import { PermissionsResponse } from "../../../../app/access-control/permissions-response";
import { WorkforceAccountContactDetails } from "../../../../components/system-admin/workforce/workforce-account-contact/workforce-account-contact-details";
import { WorkforceGeneralContact } from "../../../../components/system-admin/workforce/workforce-general-contact/workforce-general-contact";
import { WorkforceJobRoleDetails } from "../../../../components/system-admin/workforce/workforce-job-role-details/workforce-job-role-details";
import { WorkforceScheduleContactDetails } from "../../../../components/system-admin/workforce/workforce-schedule/workforce-schedule-contact-details";
import { WorkforceSkillsAndInterests } from "../../../../components/system-admin/workforce/workforce-skills-and-interests/workforce-skills-and-interests.component";
import ResourceManagementLookups from "../../../../models/common/resource-management-lookups";
import Company from "../../../../models/company-details/company";
import CognitoAccount from "../../../../models/company-resources/cognito-account";
import CognitoRole from "../../../../models/company-resources/cognito-role";
import LocationDto from "../../../../models/company-resources/location-dto";
import { BusinessPhoneNumberType } from "../../../../models/contact/business-phone-number";
import Role from "../../../../models/system-admin/roles";
import { RolesService } from "../../../../providers/roles/roles.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";

const ENTITY_TYPE = "ROLE";
const COMPANY_ID_VALUE = "000000";
const COMPANY_ID_FIELD = "companyId";

const LOCATION_LEVEL = "LOCATION";
// OPS-2858. Mirrors ResourceStatus.INACTIVE(2) in resource-management-service.
const RESOURCE_STATUS_INACTIVE = 2;

@Component({
  selector: "workforce-contact",
  templateUrl: "workforce-contact.html",
})
export class WorkforceContactPage extends CompanyResourcePage {
  @ViewChild(WorkforceGeneralContact)
  generalContactChild: WorkforceGeneralContact;
  @ViewChild(WorkforceJobRoleDetails) jobRoleChild: WorkforceJobRoleDetails;
  @ViewChild(EmployeeHealthContactDetails)
  employeeHealthChild: EmployeeHealthContactDetails;
  @ViewChild(WorkforceAccountContactDetails)
  accountChild: WorkforceAccountContactDetails;
  @ViewChild(WorkforceSkillsAndInterests)
  skillsAndInterestsChild: WorkforceSkillsAndInterests;
  @ViewChild(WorkforceScheduleContactDetails)
  scheduleChild: WorkforceScheduleContactDetails;

  contactData: CredentialedResource = new CredentialedResource();
  protected readonly ContactType = ContactType;
  _displayName: string;
  parentForm: FormGroup;
  private _isAdminstrator: boolean = true;
  _saveEnabled: boolean = false;
  _contactIsEmployee: boolean = false;
  _contactIsSystemUser: boolean = false;
  /** OPS-2858. See CredentialedResource#isSupportUser - must survive an edit. */
  contactIsSupportUser: boolean = false;
  _contactIsVolunteer: boolean = false;
  subscriptions: Subscription[] = [];
  contactType: ContactType = ContactType.PROVIDERS;
  _volunteerTypes: LookupValue[];
  _professionalRelations: LookupValue[];
  public rmLookups: ResourceManagementLookups = {} as ResourceManagementLookups;
  _isEditMode: boolean = true;
  _isNew: boolean = false;
  backupRoles: CognitoRole[] = [];
  private otherLocations: LocationDto[];
  private rolesData: Role[] = [];
  private filterParameters: any = {};
  cardHeader: string;
  cardDetailUsers: string;
  emailRegex: RegExp = commonVar.EMAIL_REGEX;

  company: Company | undefined;
  user: IUser | undefined;

  permissionsAccess = {} as PermissionsResponse;
  phoneNumbers: any[] = [];

  // CO-3511: fail closed. Nothing on this page consulted a permission before —
  // Save was gated only by form validity, so a user holding only
  // Company:Workforce:VIEW could open a resource and rewrite it. Adding needs ADD,
  // editing an existing one needs EDIT; the wildcard satisfies both.
  canSaveResource: boolean = false;

  tabs = [
    {
      id: "generalDetails",
      title: "General Details",
      selected: true,
      status: "incomplete",
    },
    {
      id: "jobRole",
      title: "Licensing and Credentials",
      selected: false,
      status: "incomplete",
    },
  ];
  activeTab = this.tabs[0];
  activeTabId = this.activeTab.id;
  deletedAddresses = [];
  deletedCredentials = [];

  jobRoleInitData = {
    credentials: [],
  };
  days = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ];

  get isAdministrator(): boolean {
    return this._isAdminstrator;
  }

  // CO-3511: this setter used to ignore `v` and assign `true` unconditionally, so
  // isAdministrator could never be turned off. It gates the "Account Setup" tab and
  // a branch of checkCanSave(), i.e. it is a gate that could not close. The only
  // assignment today is `true` (from the constructor), so honouring the argument is
  // behaviour-preserving now and removes the trap for the next caller.
  set isAdministrator(v: boolean) {
    this._isAdminstrator = v;
  }

  get saveEnabled() {
    return this._saveEnabled;
  }

  set saveEnabled(value: boolean) {
    this._saveEnabled = value;
  }

  get isEditMode() {
    return this._isEditMode;
  }

  set isNew(value: boolean) {
    this._isNew = !!value;
  }

  get isNew() {
    return this._isNew;
  }

  set isEditMode(value: boolean) {
    this._isEditMode = value;
    this.checkCanSave();
  }

  get contactUserId(): string {
    if (!this.contactData || !this.contactData.externalId) {
      return "";
    }
    return this.contactData.externalId.toString();
  }

  get contactIsEmployee() {
    return this._contactIsEmployee;
  }

  set contactIsEmployee(value: boolean) {
    this._contactIsEmployee = value ? value : false;
    this.updateTabs();
  }

  get contactIsSystemUser() {
    return this._contactIsSystemUser;
  }

  set contactIsSystemUser(value: boolean) {
    this._contactIsSystemUser = value;
    this.contactIsSystemUser
      ? this.addAccountFormControl()
      : this.removeAccountFormControl();
    this.updateTabs();
  }

  get contactIsVolunteer() {
    return this._contactIsVolunteer;
  }

  set contactIsVolunteer(value: boolean) {
    this._contactIsVolunteer = value;
    this.updateTabs();
  }

  get generalDetailsControl() {
    if (this.parentForm.contains("generalDetails")) {
      return this.parentForm.get("generalDetails") as FormControl;
    }
    return null;
  }

  get jobRoleControl() {
    if (this.parentForm.contains("jobRole")) {
      return this.parentForm.get("jobRole") as FormControl;
    }
    return null;
  }

  get employeeHealthControl() {
    if (this.parentForm.contains("employeeHealth")) {
      return this.parentForm.get("employeeHealth") as FormControl;
    }
    return null;
  }

  get accountControl() {
    if (this.parentForm.contains("account")) {
      return this.parentForm.get("account") as FormControl;
    }
  }

  get skillsAndInterestsControl() {
    if (this.parentForm.contains("skillsAndInterests")) {
      return this.parentForm.get("skillsAndInterests") as FormControl;
    }
  }

  get schedulesControl() {
    if (this.parentForm.contains("schedules")) {
      return this.parentForm.get("schedules") as FormControl;
    }
  }

  get displayName() {
    return this._displayName;
  }

  set displayName(value: string) {
    this._displayName = value;
  }

  constructor(
    protected accessControlService: AccessControlService,
    protected authSvc: AuthService,
    protected navBarSvc: NavBarServiceProvider,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    protected modalController: ModalController,
    protected resourceManagementService: ResourceManagementService,
    protected loader: LoadingController,
    protected userService: UsersService,
    protected stateManagementService: StateManagementService,
    protected patientChartService: PatientChartService,
    protected fb: FormBuilder,
    protected alertController: AlertController,
    protected events: Events,
    protected authService: AuthService,
    private utilityService: UtilityService,
    private roleService: RolesService,
    private accessCntrl: AccessControlService
  ) {
    super(
      accessControlService,
      navBarSvc,
      navCtrl,
      navParams,
      userService,
      stateManagementService,
      patientChartService,
      resourceManagementService
    );
    this.user = authService.getUser();
    this.company = authSvc.getCompany();
    this.contactData.resourceType = ContactType.PROVIDERS.toString();
    this.isAdministrator = true;

    if (this.navParams && this.navParams.data) {
      if (this.navParams.data.provider) {
        this.contactData = this.navParams.data.provider;
        this.contactType = +this.navParams.data.contactType;
        // OPS-2858: a NEW support user carries the flag on the seeded resource
        // (stamped by WorkforcePage.addCompanyResource); an EXISTING one carries it on
        // the fetched payload.
        //
        // Set ONLY the plain field here. Do NOT touch contactIsSystemUser: its setter
        // calls addAccountFormControl(), which dereferences this.parentForm - and
        // parentForm is not built until later in this same constructor. Doing it here
        // threw "Cannot read properties of undefined (reading 'contains')" and the
        // edit page never opened. The sibling contactIsVolunteer setter is safe here
        // only because it just calls updateTabs(). Forced in ngOnInit instead.
        if (
          this.contactData &&
          (this.contactData as any).isSupportUser === true
        ) {
          this.contactIsSupportUser = true;
        }
        this.contactIsVolunteer = this.contactType === ContactType.VOLUNTEERS;
      }
      if (this.navParams.data.volunteerTypes) {
        this._volunteerTypes = this.navParams.data.volunteerTypes;
      }
      this.isNew = this.navParams.data.isNew;
      if (this.isNew) {
        this.resourceManagementService.changeEmailAddress("");
      }
      // if(this.navParams.data.companyData){
      //     this.companyData = this.navParams.data.companyData;
      // }

      if (this.navParams.data.professionalRelations) {
        this._professionalRelations = this.navParams.data.professionalRelations;
      }
      if (this.navParams.data.rmLookups) {
        this.rmLookups = this.navParams.data.rmLookups;
      }
    }

    this.cardHeader = "Edit Resource";
    if (!this.isNew) {
      this.cardDetailUsers = `${this.contactData.lastName}, ${this.contactData.firstName} ${this.contactUserId}`;
    }

    if (this.isNew) {
      this.cardDetailUsers = "";
      this.cardHeader = "Add Resource";
    }

    this.parentForm = this.fb.group({
      id: [""],
      cognitoUserId: [null],
      externalId: [null],
      generalDetails: [null, Validators.required],
      jobRole: [this.jobRoleInitData, Validators.required],
      skillsAndInterests: [],
      schedules: [],
      employeeHealth: [],
    });
  }

  ngOnInit() {
    // super.ngOnInit(); //further discussion needed to check the extends of CompanyResourcePage

    // OPS-2858 decision 8: a support user is ALWAYS a login user.
    // Done HERE, not in the constructor, because the setter adds the "account" form
    // control and parentForm does not exist until the end of the constructor.
    // CompanyResourceServiceImpl skips /user-profile propagation entirely for a
    // resource that is not and is not becoming a system user, so without this a
    // support user saves with no login and ZERO OrgRoles - granting nothing.
    if (this.contactIsSupportUser) {
      this.contactIsSystemUser = true;
    }

    this.updateTabs();
    this.loadRoles();

    this.subscriptions.push(
      this.accessCntrl
        .getPermission(PERMISSION_GROUPS_COGNITO)
        .subscribe((data) => {
          this.permissionsAccess = data;
          this.checkSavePermission();
        })
    );
  }

  checkSavePermission() {
    const hasAll = this.accessCntrl.hasAccess(
      this.permissionsAccess,
      Workforce.CompanyWorkforceAll
    );
    const required = this.isNew
      ? Workforce.CompanyWorkforceAdd
      : Workforce.CompanyWorkforceEdit;

    const basePermission =
      hasAll || this.accessCntrl.hasAccess(this.permissionsAccess, required);

    // OPS-2858: saving a SUPPORT USER additionally requires
    // UserProfile.IsSupportUserAdmin. The grid already hides the pencil, but this
    // page is reachable by deep link and CO-3511 established that hiding a control
    // is a hint, not a gate. Fails closed - undefined behaves as false.
    if (this.contactIsSupportUser) {
      const user = this.authSvc.getUser();
      this.canSaveResource =
        basePermission && !!user && user.isSupportUserAdmin === true;
      return;
    }

    this.canSaveResource = basePermission;
  }

  ionViewWillEnter() {
    if (this._isEditMode) {
      this.loadData(this.contactData);
    }
    if (!this.parentForm.value.id) {
      this.isEditMode = true;
    }
  }

  onFormChanges = (value) => {
    if (value.generalDetails) {
      this.contactIsEmployee =
        value.generalDetails.professionalRelation &&
        value.generalDetails.professionalRelation === 2;
      this.contactType = value.generalDetails.resourceType
        ? +value.generalDetails.resourceType
        : +this.contactType;
      this.contactIsVolunteer = this.contactType === ContactType.VOLUNTEERS;
      if (this.contactData) {
        let valSystemUser = value.generalDetails.systemUser;
        if (typeof valSystemUser === "string") {
          valSystemUser = value.generalDetails.systemUser === "true";
          this.contactIsSystemUser = valSystemUser;
        }
      }
      this.generalDetailsControl.get("workPhoneNumbers")?.markAsDirty();
      this.generalDetailsControl
        .get("workPhoneNumbers")
        ?.updateValueAndValidity();
    }

    this.buildDisplayName(value);
    this.checkCanSave();
    this.updateTabStatus();
  };

  buildDisplayName(value) {
    if (value.generalDetails) {
      const { lastName, firstName, middleInitial, preferredName } =
        value.generalDetails;

      let displayName = [];
      if (lastName) {
        displayName.push(lastName + (firstName ? "," : ""));
      }
      if (firstName) {
        displayName.push(firstName);
      }
      if (middleInitial) {
        displayName.push(`${middleInitial}.`);
      }
      if (preferredName) {
        displayName.push(`"${preferredName}"`);
      }
      this.displayName = displayName.join(" ");
    }
  }

  ionViewCanEnter() {
    return this.authSvc.authenticated();
  }

  ionViewDidLoad() {
    this.evaluateAccess();
    this.stateManagementService.setAssessmentType("Resource Management");
  }

  checkCanSave() {
    const generalValid =
      this.generalDetailsControl && this.generalDetailsControl.valid;
    const jobRoleValid = this.jobRoleControl && this.jobRoleControl.valid;
    const employeeHealthValid = this.contactIsEmployee
      ? this.employeeHealthControl && this.employeeHealthControl.valid
      : true;
    let allValid = generalValid && jobRoleValid && employeeHealthValid;
    if (this.contactIsSystemUser && this.isAdministrator) {
      const accountValid = this.accountControl && this.accountControl.valid;
      allValid = allValid && accountValid;
    }
    if (this.contactIsVolunteer) {
      const skillsAndInterestsValid =
        this.skillsAndInterestsControl && this.skillsAndInterestsControl.valid;
      const scheduleValid =
        this.schedulesControl && this.schedulesControl.valid;
      allValid = allValid && skillsAndInterestsValid && scheduleValid;
    }

    allValid = allValid && this.generalContactChild.primaryLocations.length > 0;

    if (this.parentForm.contains("account")) {
      // allValid = allValid && this.companyData && this.companyData.companyId && this.rolesData.length > 0;
      allValid =
        allValid && this.company.companyId !== "" && this.rolesData.length > 0;
    }

    this.saveEnabled = allValid;
  }

  loadData(data: CredentialedResource) {
    if (data && data.id) {
      this.contactType = +data.resourceType;
      this.contactIsSystemUser = data.isSystemUser;
      // OPS-2858: remembered so save() can round-trip it. The server maps this onto
      // contacts.is_support_user, so omitting it on PUT drops the user out of the
      // Support Users tab - a silent reclassification with no error.
      this.contactIsSupportUser = data.isSupportUser === true;
      // OPS-2858: the save gate depends on this flag, and the permission response can
      // land before loadData does. Recompute rather than leave it stale.
      this.checkSavePermission();
      if (this.contactIsSupportUser) {
        // OPS-2858 decision 8: support users are ALWAYS login users.
        // CompanyResourceServiceImpl skips /user-profile propagation entirely for a
        // resource that is not and is not becoming a system user, so without this a
        // support user saves with no login and ZERO OrgRoles - granting nothing,
        // silently.
        this.contactIsSystemUser = true;
      }
      this.contactIsEmployee = data.professionalRelation === 2;
      this.contactIsVolunteer = +data.resourceType === ContactType.VOLUNTEERS;
      if (data.contactLocations && data.splitLocations()) {
        if (data.otherLocations) this.otherLocations = data.otherLocations;
        this.generalContactChild.setLocationsSelected(
          data.location,
          data.otherLocations
        );
      }
      if (data.employeeInformation) {
        this.flattenEmployeeContactData(data);
      }
      if (!data.addresses) {
        data.addresses = [];
      }
      if (!data.phoneNumbers) {
        data.phoneNumbers = [];
      }
      if (data.phoneNumbers && data.phoneNumbers.length > 0) {
        data.phoneNumbers = data.phoneNumbers.filter((x) => !x.deleted);
        this.phoneNumbers = data.phoneNumbers;
      }
      this.initializePhoneNumbers(data);

      this.parentForm.patchValue({
        generalDetails: { ...data, systemUser: data.isSystemUser },
      });
      this.parentForm.patchValue({
        generalDetails: {
          ...data,
          systemUser: data.isSystemUser,
          resourceType: +data.resourceType,
          resourceStatus: data.resourceStatus,
        },
      });
      this.parentForm.patchValue({ jobRole: { ...data } });
      this.parentForm.patchValue({ id: data.id });
      this.parentForm.patchValue({ cognitoUserId: data.cognitoUserId });
      this.parentForm.patchValue({ externalId: data.externalId });

      if (data instanceof Volunteer) {
        if (data.preferences) {
          let otherTerm = null;
          let ski = data.preferences.skillsAndInterests;
          if (ski) {
            for (let sIndex = 0; sIndex < ski.length; sIndex++) {
              if (ski[sIndex].term === "Other" && ski[sIndex].other != null) {
                otherTerm = ski[sIndex].other;
                ski.splice(sIndex, 1);
              }
            }
          }
          const skillsAndInterests = data.preferences.skillsAndInterests.map(
            (ski) => ski.term
          );

          this.parentForm.patchValue({
            skillsAndInterests: {
              skillsAndInterests: skillsAndInterests,
              languages: data.languages,
              other: otherTerm,
            },
          });
          let schedules = this.mapScheduleFromScheduleData(
            data.preferences.schedule
          );
          this.parentForm.patchValue({
            schedules: { schedules: schedules },
          });

          let volunteerTypes = this.unMaskVolunteerType(data.volunteerType);
          this.parentForm.patchValue({
            jobRole: {
              ...data,
              volunteerType: volunteerTypes,
              trainingCompletionDate: data.trainingCompletionDate,
            },
          });
        }
      }

      if (data.employeeInformation && data.employeeInformation.healthDetails) {
        this.parentForm.patchValue({
          employeeHealth: {
            vaccinations: data.employeeInformation.healthDetails,
          },
        });
      }

      if (
        data.employeeInformation &&
        data.employeeInformation.employeeAccount &&
        this._contactIsSystemUser
      ) {
        this.backupRoles =
          data.employeeInformation.employeeAccount.employeeAccountDetails;
        let roles =
          data.employeeInformation.employeeAccount.employeeAccountDetails.map(
            (a) => {
              return a.roleId;
            }
          );
        let disciplines =
          data.employeeInformation.employeeAccount
            .employeeAccountDisciplines[0];
        let department = data.employeeInformation.employeeAccount.department;
        let position = data.employeeInformation.employeeAccount.position;
        let cognitoUserId = data.cognitoUserId ? data.cognitoUserId : null;
        let accountName = data.emailAddress ? data.emailAddress : "";
        this.parentForm.patchValue({
          account: {
            employeeAccountDetails: roles,
            position: position,
            employeeAccountDisciplines: disciplines,
            department: department,
            cognitoUserId: cognitoUserId,
            accountName: accountName,
          },
        });
        this.parentForm.get("account").markAsTouched();
      }
      this.generalContactChild.innerFormGroup.patchValue(
        this.generalDetailsControl.value
      );
      this.parentForm.updateValueAndValidity();
      this.recheckJobRole();
    } else {
      this._isNew = true;
      this.initializePhoneNumbers(data);
      this.contactIsVolunteer = this.contactType === ContactType.VOLUNTEERS;
      this.parentForm.patchValue({
        generalDetails: { resourceType: +this.contactType, resourceStatus: 1 },
      });
      this.generalContactChild.innerFormGroup.patchValue(
        this.generalDetailsControl.value
      );
      this.parentForm.updateValueAndValidity();
    }
    this.subscriptions.push(
      this.parentForm.valueChanges.subscribe(this.onFormChanges)
    );
    this.parentForm.updateValueAndValidity();
  }

  private initializePhoneNumbers(data: CredentialedResource) {
    if (!data.workPhoneNumbers) data.workPhoneNumbers = [];
    if (!data.cellPhoneNumbers) data.cellPhoneNumbers = [];
    if (!data.faxPhoneNumbers) data.faxPhoneNumbers = [];
    if (data.workPhoneNumbers.length === 0) {
      data.workPhoneNumbers.push({
        id: null,
        phoneNumber: null,
        phoneNumberExtension: "",
        phoneNumberType: BusinessPhoneNumberType.WORK,
        deleted: false,
        primary: false,
      });
    }
    if (data.cellPhoneNumbers.length === 0) {
      data.cellPhoneNumbers.push({
        id: null,
        phoneNumber: null,
        phoneNumberExtension: "",
        phoneNumberType: BusinessPhoneNumberType.CELL,
        deleted: false,
        primary: false,
      });
    }
    if (data.faxPhoneNumbers.length === 0) {
      data.faxPhoneNumbers.push({
        id: null,
        phoneNumber: null,
        phoneNumberExtension: "",
        phoneNumberType: BusinessPhoneNumberType.FAX,
        deleted: false,
        primary: false,
      });
    }
  }

  toggleIsEditMode() {
    this._isEditMode = !this._isEditMode;
    this.recheckJobRole();
  }

  close() {
    this.navCtrl.pop();
  }

  cancel() {
    let alert = this.alertController.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel?",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.navCtrl.pop();
          },
        },
      ],
    });
    alert.present();
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
          handler: () => {},
        },
      ],
    });
    alert.present();
  };

  save() {
    // CO-3511: boundary guard. The template disables the button without the
    // permission, but a disabled button is a hint, not a gate.
    if (!this.canSaveResource) {
      this.showAlert({
        title: "Not Permitted",
        message: "You do not have permission to save changes to this resource.",
      });
      return;
    }

    this.checkCanSave();
    if (this.saveEnabled) {
      let obj = this.mapCompanyResourceFromForm(this.parentForm.value);

      if (this.parentForm.value.id > 0) {
        this.update(obj);
      } else {
        this.create(obj);
      }
    } else {
      this.showAlert({
        title: "Invalid Submission",
        message: "Missing required fields. Please review.",
      });
    }
  }

  private create(obj) {
    this.saveEnabled = false;
    this.utilityService.increaseSpinnerCount("Creating Resource...");
    return this.resourceManagementService
      .createProfessionalProvider(obj)
      .pipe()
      .take(1)
      .subscribe((o) => {
        this.onSaveSuccess();
      }, this.onSaveError);
  }

  private update(obj) {
    this.saveEnabled = false;
    this.utilityService.increaseSpinnerCount("Updating Resource...");
    this.resourceManagementService
      .updateProfessionalProvider(this.contactData.id, obj)
      .pipe()
      .take(1)
      .subscribe(this.onSaveSuccess, this.onSaveError);
  }

  private onSaveSuccess = () => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (this.navParams.data.parent) {
      this.navParams.data.parent.loadProfessionalProviders(0);
    }
    this.navCtrl.pop();
  };

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: error.message,
        });
      }
    }
  };

  flattenEmployeeContactData(data): void {
    data.driversLicenseNumber = data.employeeInformation.driversLicenseNumber;
    data.hireDate = data.employeeInformation.hireDate;
    data.employeeAltId = data.employeeInformation.employeeAltId;
    data.driversLicenseExpiration =
      data.employeeInformation.driversLicenseExpiration;
    data.payrollId = data.employeeInformation.payrollId;
    if (data.employeeInformation.jobRoleDetails) {
      data.employmentStatus =
        data.employeeInformation.jobRoleDetails.employmentStatus;
    }
  }

  mapCompanyResourceFromForm(form: any): WorkforceResource {
    let cr = new WorkforceResource();
    cr.resourceType = +form.generalDetails.resourceType;
    cr.professionalRelation = form.generalDetails.professionalRelation;
    cr.resourceStatus = form.generalDetails.resourceStatus;
    cr.firstName = form.generalDetails.firstName;
    cr.lastName = form.generalDetails.lastName;
    cr.employedBy = form.generalDetails.employedBy;
    cr.middleInitial = form.generalDetails.middleInitial;
    cr.preferredName = form.generalDetails.preferredName;
    this.generalDetailsTrim(cr);
    cr.cognitoUserId = form.cognitoUserId ? form.cognitoUserId : null;
    cr.externalId = form.externalId ? form.externalId : null;
    cr.emailAddress = form.generalDetails.emailAddress
      ? form.generalDetails.emailAddress.toLowerCase()
      : form.generalDetails.emailAddress;
    cr.groupAffiliation = form.generalDetails.groupAffiliation;
    cr.addresses = form.generalDetails.addresses.concat(this.deletedAddresses);
    cr.phoneNumbers = form.generalDetails.phoneNumbers.concat(
      form.generalDetails.deletedPhoneNumbers
    );
    cr.notes = form.generalDetails.notes;
    cr.employedBy = form.generalDetails.employedBy;
    cr.effectiveDate = form.generalDetails.effectiveDate
      ? moment(form.generalDetails.effectiveDate).format("YYYY-MM-DD")
      : form.generalDetails.effectiveDate;
    cr.suffix = +form.generalDetails.suffix;
    cr.gender = form.generalDetails.gender;
    cr.companyId =
      this.generalContactChild.innerFormGroup.get("companyId").value;
    cr.companyName =
      this.generalContactChild.innerFormGroup.get("companyName").value;
    cr.contactLocations = [];
    cr.contactLocations.push(form.generalDetails.location);
    if (
      form.generalDetails.otherLocations &&
      form.generalDetails.otherLocations.length > 0
    ) {
      cr.contactLocations = cr.contactLocations.concat(
        form.generalDetails.otherLocations
      );
    }
    if (
      this.user.selectedOrgType === LOCATION_LEVEL &&
      this.otherLocations &&
      form.generalDetails.otherLocations.length === 0
    ) {
      cr.contactLocations = cr.contactLocations.concat(this.otherLocations);
    }

    // OPS-2858 - OFFBOARDING. Deactivating a support user must also REVOKE their
    // cross-company access, not merely mark them inactive.
    //
    // Inactive status alone does not revoke anything: iam-functions never consults
    // Status, IsDeleted or IsMigrated (verified - no matches in that service), so a
    // deactivated support user with a valid JWT keeps being evaluated exactly as
    // before. The only thing that actually shrinks their access is a smaller
    // OrgRoles list.
    //
    // Submitting a primary-only set does that, by deliberately using the existing
    // wholesale-replace behaviour: PUT /company-resources replaces contactLocations
    // and orphanRemoval = true hard-deletes the omitted rows, which the backend then
    // cross-products into a one-entry OrgRoles. The primary is kept because
    // PrimaryOrgId is validate:"required" server-side and getTenantIdFromMainLocation
    // derives contacts.tenant_id from it - dropping it would 400 or re-tenant the row.
    //
    // Envers keeps the removed rows in contact_locations_aud, so this is recoverable.
    //
    // RESIDUAL, and it belongs in the runbook: their existing access token keeps
    // working until it expires, and iam-functions caches permission answers in
    // Momento for up to 60 minutes with no invalidation path. "Deactivated" is not
    // instant.
    if (
      this.contactIsSupportUser &&
      +form.generalDetails.resourceStatus === RESOURCE_STATUS_INACTIVE
    ) {
      cr.contactLocations = form.generalDetails.location
        ? [form.generalDetails.location]
        : [];
    }

    if (form.jobRole) {
      if (this.contactType === 1) {
        cr.npi = form.jobRole.npi;
        cr.dea = form.jobRole.dea;
        cr.deaStartDate = form.jobRole.deaStartDate
          ? moment(form.jobRole.deaStartDate).format("YYYY-MM-DD")
          : form.jobRole.deaStartDate;
        cr.deaExpireDate = form.jobRole.deaExpireDate
          ? moment(form.jobRole.deaExpireDate).format("YYYY-MM-DD")
          : form.jobRole.deaExpireDate;
        cr.taxId = form.jobRole.taxId;
        cr.taxonomyCode = form.jobRole.taxonomyCode;
      } else if (this.contactIsVolunteer) {
        let maskVolunteerType = 0;
        form.jobRole.volunteerType.forEach((value) => {
          maskVolunteerType += value;
        });
        cr.volunteerType = maskVolunteerType;

        cr.trainingCompletionDate = form.jobRole.trainingCompletionDate
          ? moment(form.jobRole.trainingCompletionDate).format()
          : "";

        let schedule: VolunteerSchedule;
        if (form.schedules && form.schedules.schedules) {
          for (let j = 0; j < form.schedules.schedules.length; j++) {
            let availabilities: VolunteerAvailabilityPreference[] = [];
            for (let i = 0; i <= 6; i++) {
              let day: VolunteerAvailabilityPreference = {
                day: "",
                selected: false,
                morning: false,
                afternoon: false,
                evening: false,
              };
              day.day = i.toString();
              let s = form.schedules.schedules[j];
              if (s.morning && s.morningTime.includes(i)) {
                day.morning = true;
                day.selected = true;
              }
              if (s.afternoon && s.afternoonTime.includes(i)) {
                day.afternoon = true;
                day.selected = true;
              }
              if (s.evening && s.eveningTime.includes(i)) {
                day.evening = true;
                day.selected = true;
              }

              availabilities.push(day);
            }
            availabilities = availabilities.filter((a) => a.selected);
            let locationPreferences: VolunteerLocationPreference[] = [];
            locationPreferences.push({
              radius: form.schedules.schedules[j].radius,
              zipCode: form.schedules.schedules[j].zip,
              radiusMeasurement: RadiusMeasurement.MILE,
            });
            schedule = new VolunteerSchedule(
              availabilities,
              locationPreferences
            );
          }
        }

        let skillsAndInterests: VolunteerSkillInterest[] = [];
        if (
          form.skillsAndInterests &&
          form.skillsAndInterests.skillsAndInterests
        ) {
          skillsAndInterests = form.skillsAndInterests.skillsAndInterests.map(
            (ski) => ({
              term: ski,
              selected: true,
              type: "skillInterest",
            })
          );
          cr.languages = form.skillsAndInterests.languages;
        }
        if (form.skillsAndInterests && form.skillsAndInterests.other) {
          // CO-3511: this used to split on a single space and index word[0] with no
          // guard, so a double space (or a leading/trailing one) produced an empty
          // segment and `""[0]` was undefined — Save threw and the volunteer was
          // never persisted, with no error surfaced. Split on runs of whitespace and
          // drop empties instead.
          let normalize = form.skillsAndInterests.other
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0);
          let otherOption = normalize
            .map((word) => {
              return word[0].toUpperCase() + word.substring(1).toLowerCase();
            })
            .join(" ");

          skillsAndInterests.push({
            term: "Other",
            selected: true,
            other: otherOption,
          });
        }

        cr.preferences = new VolunteerPreferences(schedule, skillsAndInterests);
      }
      if (form.jobRole.credentials) {
        cr.credentials = form.jobRole.credentials
          .concat(this.deletedCredentials)
          .map((c) => {
            c.validFromDatetime = c.validFromDatetime
              ? moment.utc(c.validFromDatetime).format()
              : "";
            c.validToDatetime = c.validToDatetime
              ? moment.utc(c.validToDatetime).format()
              : "";
            return c;
          });
      }
      if (form.jobRole.disciplines) {
        let ed = new EmployeeDetail();
        ed.jobRoleDetails = {
          ...ed.jobRoleDetails,
          disciplines: form.jobRole.disciplines
            ? form.jobRole.disciplines.map((d) => {
                d.startDate = d.startDate
                  ? moment.utc(d.startDate).format()
                  : "";
                d.endDate = d.endDate ? moment.utc(d.endDate).format() : "";
                return d;
              })
            : [],
        };

        cr.employeeInformation = ed;
      }
    }
    if (this.contactIsEmployee) {
      let ed = new EmployeeDetail();
      ed.hireDate = form.generalDetails.hireDate
        ? moment(form.generalDetails.hireDate).format("YYYY-MM-DD")
        : "";
      ed.employeeAltId = form.generalDetails.employeeAltId;
      ed.payrollId = form.generalDetails.payrollId;
      ed.driversLicenseNumber = form.generalDetails.driversLicenseNumber;
      ed.driversLicenseExpiration = form.generalDetails.driversLicenseExpiration
        ? moment(form.generalDetails.driversLicenseExpiration).format(
            "YYYY-MM-DD"
          )
        : "";
      ed.jobRoleDetails = {
        employmentStatus: form.jobRole.employmentStatus,
        department: form.jobRole.department,
        position: form.jobRole.position,
        disciplines: form.jobRole.disciplines
          ? form.jobRole.disciplines.map((d) => {
              d.startDate = d.startDate ? moment.utc(d.startDate).format() : "";
              d.endDate = d.endDate ? moment.utc(d.endDate).format() : "";
              return d;
            })
          : [],
      };
      if (form.employeeHealth) {
        ed.healthDetails = form.employeeHealth.vaccinations.map((v) => {
          v.dateGiven = v.dateGiven
            ? moment(v.dateGiven).format("YYYY-MM-DD")
            : "";
          v.dateNextRequired = v.dateNextRequired
            ? moment(v.dateNextRequired).format("YYYY-MM-DD")
            : "";
          v.dateRead = v.dateRead
            ? moment(v.dateRead).format("YYYY-MM-DD")
            : "";
          return v;
        });
      }
      cr.employeeInformation = ed;
    }

    if (this.contactIsEmployee || this.contactIsVolunteer) {
      cr.dateOfBirth = form.generalDetails.dateOfBirth
        ? moment.utc(form.generalDetails.dateOfBirth).format()
        : "";
      cr.primaryLanguage = form.generalDetails.primaryLanguage;
    }

    cr.isSystemUser = this.contactIsSystemUser;
    cr.isSupportUser = this.contactIsSupportUser;
    if (this.contactIsSystemUser) {
      if (form.account) {
        cr.employeeInformation = cr.employeeInformation
          ? cr.employeeInformation
          : new EmployeeDetail();
        cr.employeeInformation.employeeAccount = new CognitoAccount();
        if (form.account.department) {
          cr.employeeInformation.employeeAccount.department =
            form.account.department;
        }
        if (form.account.position) {
          cr.employeeInformation.employeeAccount.position =
            form.account.position;
        }
        if (form.account.employeeAccountDisciplines) {
          cr.employeeInformation.employeeAccount.employeeAccountDisciplines = [
            form.account.employeeAccountDisciplines,
          ];
        }
        if (form.account.cognitoUserId) {
          cr.cognitoUserId = form.account.cognitoUserId;
        }
      }

      // CO-3511: this block used to sit outside the `if (form.account)` guard above,
      // so a system user with no account sub-form threw here and the save died with
      // no feedback.
      if (form.account && form.account.employeeAccountDetails) {
        let roles: CognitoRole[] = [];

        form.account.employeeAccountDetails.forEach((r) => {
          let role = this.rolesData.find((e) => e.id === r);
          if (role) {
            let auxRole = this.backupRoles.find((e) => e.roleId === role.id);
            roles.push(auxRole ? auxRole : new CognitoRole(role.id, role.name));
            return;
          }

          // CO-3511: the roles lookup is capped at 50 rows (loadFilters), and a miss
          // used to be skipped silently — so a system user whose role sat past that
          // cap lost the assignment on the next unrelated save, with no error and no
          // visible clue. If we already hold the persisted assignment, keep it rather
          // than dropping it just because the capped lookup did not echo it back.
          let existing = this.backupRoles.find((e) => e.roleId === r);
          if (existing) {
            roles.push(existing);
          }
        });
        cr.employeeInformation.employeeAccount.employeeAccountDetails = roles;
      }
    } else {
      cr.employeeInformation = cr.employeeInformation
        ? cr.employeeInformation
        : undefined;
    }
    return cr;
  }

  private generalDetailsTrim(cr: WorkforceResource) {
    cr.firstName = this.trimProperty(cr.firstName);
    cr.lastName = this.trimProperty(cr.lastName);
    if (cr.preferredName) {
      cr.preferredName = this.trimProperty(cr.preferredName);
    }
    if (cr.employedBy) {
      cr.employedBy = this.trimProperty(cr.employedBy);
    }
    if (cr.middleInitial) {
      cr.middleInitial = this.trimProperty(cr.middleInitial);
    }
  }

  private trimProperty(str: string): string {
    // CO-3511: firstName/lastName are trimmed unconditionally by generalDetailsTrim,
    // so an absent value threw here rather than failing validation.
    if (typeof str !== "string") {
      return str;
    }
    return str.replace(/\n/g, "").replace(/\r/g, "").trim();
  }

  mapScheduleFromScheduleData(scheduleData: any) {
    let schedules = [];

    if (scheduleData.availabilities.length > 0) {
      let schedule: any = {
        morning: false,
        afternoon: false,
        evening: false,
        morningTime: [],
        afternoonTime: [],
        eveningTime: [],
        zip:
          scheduleData.locationPreferences.length > 0
            ? scheduleData.locationPreferences[0].zipCode
            : null,
        radius:
          scheduleData.locationPreferences.length > 0
            ? scheduleData.locationPreferences[0].radius
            : null,
      };

      for (let a of scheduleData.availabilities) {
        if (a.morning) {
          schedule.morning = true;
          schedule.morningTime.push(this.days.indexOf(a.day));
        }
        if (a.afternoon) {
          schedule.afternoon = true;
          schedule.afternoonTime.push(this.days.indexOf(a.day));
        }
        if (a.evening) {
          schedule.evening = true;
          schedule.eveningTime.push(this.days.indexOf(a.day));
        }
      }
      schedules.push(schedule);
    }
    return schedules;
  }

  unMaskVolunteerType(volunteerType) {
    let volunteerTypes = [];
    if (volunteerType > 0) {
      for (let i = 0; i < 4; i++) {
        if (volunteerType % 2) {
          volunteerTypes.push(Math.pow(2, i));
        }
        volunteerType = Math.trunc(volunteerType / 2);
      }
    }
    return volunteerTypes;
  }

  // Tab Managemnent
  getTabStatus(control: FormControl) {
    let formContainsValue = control.value
      ? Object.keys(control.value).filter(
          (x) =>
            control.value[x] !== undefined &&
            control.value[x] !== null &&
            control.value[x] !== "" &&
            !(Array.isArray(control.value[x]) && control.value[x].length === 0)
        ).length > 0
      : false;
    if (formContainsValue && control.invalid === true) {
      return "incomplete";
    } else if (formContainsValue && control.valid === true) {
      return "complete";
    } else return "blank";
  }

  updateTabStatus() {
    this.tabs = this.tabs.map((tab) => {
      if (this.parentForm.contains(tab.id)) {
        tab.status = this.getTabStatus(
          this.parentForm.get(tab.id) as FormControl
        );
      }
      return tab;
    });
  }

  selectTab(selectedTab) {
    // update tabs
    this.tabs = this.tabs.map((tab) => {
      tab.selected = tab.id === selectedTab.id;
      return tab;
    });

    // update activeTab key for sections displayed
    this.activeTab = selectedTab;
    this.activeTabId = selectedTab.id;
  }

  updateTabs() {
    const accountTabIndex = this.tabs.findIndex((tab) => {
      return tab.id === "account";
    });

    this.tabs[1].title = this.contactIsVolunteer
      ? "Activity Type and Credentials"
      : "Licensing and Credentials";

    if (
      this.contactIsSystemUser &&
      this.isAdministrator &&
      accountTabIndex === -1
    ) {
      this.tabs.push({
        id: "account",
        title: "Account Setup",
        selected: false,
        status: "blank",
      });
    } else if (!this.contactIsSystemUser && accountTabIndex > -1) {
      this.tabs.splice(accountTabIndex, 1);
    }

    const skillsAndInterestsTabIndex = this.tabs.findIndex((tab) => {
      return tab.id === "skillsAndInterests";
    });

    if (this.contactIsVolunteer && skillsAndInterestsTabIndex === -1) {
      this.tabs.push({
        id: "skillsAndInterests",
        title: "Skills and Interests",
        selected: false,
        status: "blank",
      });
    } else if (!this.contactIsVolunteer && skillsAndInterestsTabIndex > -1) {
      this.tabs.splice(skillsAndInterestsTabIndex, 1);
    }

    const schedulesTabIndex = this.tabs.findIndex((tab) => {
      return tab.id === "schedules";
    });

    if (this.contactIsVolunteer && schedulesTabIndex === -1) {
      this.tabs.push({
        id: "schedules",
        title: "Schedule",
        selected: false,
        status: "blank",
      });
    } else if (!this.contactIsVolunteer && schedulesTabIndex > -1) {
      this.tabs.splice(schedulesTabIndex, 1);
    }
  }

  // Recheck NPI from Job Role (Async Validation)
  recheckJobRole() {
    this.jobRoleControl.updateValueAndValidity();
    this.checkCanSave();
  }

  // Account (System User)
  addAccountFormControl() {
    if (!this.parentForm.contains("account")) {
      this.parentForm.addControl(
        "account",
        this.fb.control(null, Validators.required)
      );
    }
  }

  removeAccountFormControl() {
    // CO-3511: `generalDetails` starts out null (it is only populated once loadData
    // patches it), so reading `.systemUser` off it threw whenever the system-user
    // flag was cleared before the form had been hydrated.
    const generalDetails = this.generalDetailsControl
      ? this.generalDetailsControl.value
      : null;

    if (
      this.parentForm.contains("account") &&
      (!generalDetails ||
        (generalDetails.systemUser !== null && !generalDetails.systemUser))
    ) {
      this.parentForm.removeControl("account");
    }
  }

  deleteAddress(address: any) {
    this.deletedAddresses.push(address);
  }

  deleteCredential(credential: any) {
    this.deletedCredentials.push(credential);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    super.ngOnDestroy();
  }

  goBackWorkforce() {
    this.navCtrl.pop();
  }

  loadRoles() {
    try {
      this.utilityService.increaseSpinnerCount("Loading Roles...");
      this.rolesData = [];
      this.loadFilters();

      this.roleService.getUsersRoles(this.filterParameters).subscribe(
        (response) => {
          this.rolesData = [];
          if (response && response["hits"] && response["totalHits"] > 0) {
            this.rolesData = response["hits"] as Role[];
          }
        },
        (err) => {
          this.utilityService.decreaseSpinnerCount();
          let msg = "Error loading roles.";
          this.utilityService.presentErrorToast(err, msg);
        },
        () => {
          this.utilityService.decreaseSpinnerCount();
        }
      );
    } catch (e) {
      console.log("An error has occurred: ", e);
      this.utilityService.decreaseSpinnerCount();
    }
  }

  loadFilters() {
    this.filterParameters = {};
    this.filterParameters.entityType = ENTITY_TYPE;
    this.filterParameters.from = 0;
    this.filterParameters.size = 50;
    this.filterParameters.optionalFields = [
      {
        name: COMPANY_ID_FIELD,
        value: COMPANY_ID_VALUE,
      },
      {
        name: COMPANY_ID_FIELD,
        value: this.company.companyId,
      },
    ];
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/workforce/workforce-contact/workforce-contact.ts